/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { IModuleProps, INodeProps, NodeTag } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { observer } from 'mobx-react';
import * as React from 'react';
import { IConditionalTabItemData } from './conditional-tab-item.data';
import { IConditionalTabItemProps } from './conditional-tab-item.props.autogenerated';

export interface IConditionalTabItemContentProps {
    onVisibilityChange(visibilityState: VisibilityState): void;
}

export interface ITabItemViewProps extends IConditionalTabItemContentProps {
    TabContentContainer: IModuleProps;
    TabHeaderContainer: IModuleProps;
    tabSlots: INodeProps;
    TabItemContent: INodeProps;
    tabItemType?: TabItemType;
    tabHeader: React.ReactNode;
}

export interface ITabItemExtentedProps extends IConditionalTabItemProps<IConditionalTabItemData> {
    tabItemType?: TabItemType;
    isSelected?: boolean;
    itemIndex?: number;
    tabId?: string;
    onVisibilityChange?(itemIndex: number, visibilityState: VisibilityState): void;
}

export enum TabItemType {
    TabHeader,
    TabContent
}

/**
 *
 * TabItem component.
 * @extends {React.Component<ITabItemExtentedProps>}
 */
@observer
class ConditionalTabItem extends React.Component<ITabItemExtentedProps> {
    private readonly moduleContentClassName: string = 'ms-tab-item-content';

    private readonly moduleHeaderClassName: string = 'ms-tab-item-header';

    constructor(props: IConditionalTabItemProps<IConditionalTabItemData>) {
        super(props);
        this._renderTabHeader = this._renderTabHeader.bind(this);
    }

    public render(): JSX.Element {
        const { slots, renderView, config, data, isSelected, itemIndex, onVisibilityChange } = this.props;
        const index = itemIndex || 0;
        const contentID = getContentID(index, this.props.tabId || '');
        const activeContentIDResult = data && data.activeTabState && data.activeTabState.result;
        const shown =
            activeContentIDResult &&
            activeContentIDResult.tabId === this.props.tabId &&
            activeContentIDResult.activeContentIndex !== undefined
                ? activeContentIDResult.activeContentIndex === index
                : isSelected || false;
        const viewProps = {
            ...this.props,
            TabContentContainer: {
                moduleProps: this.props,
                className: classnames(this.moduleContentClassName, config.className)
            },
            TabHeaderContainer: {
                moduleProps: this.props,
                className: classnames(this.moduleHeaderClassName, config.className)
            },
            TabItemContent: {
                className: 'ms-tab-item-content__content',
                tag: 'section' as NodeTag,
                'aria-hidden': !shown,
                role: 'tabpanel',
                tabIndex: shown ? 0 : -1,
                id: contentID
            },
            tabHeader: this._renderTabHeader(),
            tabSlots: slots,
            onVisibilityChange: (visibilityState: VisibilityState) => onVisibilityChange && onVisibilityChange(index, visibilityState)
        };
        return renderView(viewProps) as React.ReactElement;
    }

    private _renderTabHeader(): React.ReactNode {
        return <>{this.props.config.tabItemTitle}</>;
    }
}

export const getContentID = (index: number, id: string) => {
    return `tab-item-content-${id}-${index}`;
};

export default ConditionalTabItem;
